// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { OrderModel, OrderSettingsModel } from "@insite/client-framework/Types/ApiModels";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { DataTableCell, DataTableRow } from "@insite/mobius/index";
import { LinkPresentationProps } from "@insite/mobius/Link";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
    // XNG-Changes
    isVmiOrderHistoryPage: state.pages.orderHistory.isVmiOrderHistoryPage,
    showPoNumber: getSettingsCollection(state).orderSettings.showPoNumber,
});

interface PropsObj {
    show: boolean;
    title: string;
}
export interface RecentOrderTableProperties {
    orderNumber: PropsObj;
    date: PropsObj;
    orderTotal: PropsObj;
    status: PropsObj;
    shipTo: PropsObj;
    pONumber: PropsObj;
    webOrderNumber: PropsObj;
}

interface OwnProps {
    order: OrderModel;
    orderSettings: OrderSettingsModel;
    extendedStyles?: OrderSummaryCardStyles;
    // XNG-Changes
    tableProperties?: RecentOrderTableProperties;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export interface OrderSummaryCardStyles {
    orderSummaryCardGridItem?: GridItemProps;
    container?: GridContainerProps;
    pageTypeLinkGridItem?: GridItemProps;
    orderNumberGridItem?: GridItemProps;
    orderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    statusGridItem?: GridItemProps;
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    webOrderNumberGridItem?: GridItemProps;
    webOrderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    totalGridItem?: GridItemProps;
    totalHeadingAndText?: SmallHeadingAndTextStyles;
    // XNG-Changes
    clickableCell?: LinkPresentationProps;
    dataCells?: DataTableCellProps;
    shipToCells?: DataTableCellProps;
}

export const orderSummaryCardStyles: OrderSummaryCardStyles = {
    orderSummaryCardGridItem: {
        width: 12,
        css: css`
            padding: 0 20px;
            max-width: 500px;
        `,
    },
    container: { gap: 5 },
    pageTypeLinkGridItem: { width: 12 },
    orderNumberGridItem: { width: 6 },
    statusGridItem: { width: 6 },
    webOrderNumberGridItem: { width: 6 },
    totalGridItem: { width: 6 },
};

const OrderSummaryCard: FC<Props> = ({
    language,
    order,
    orderSettings,
    extendedStyles,
    // XNG-Changes
    isVmiOrderHistoryPage,
    tableProperties,
    showPoNumber,
}) => {
    const [styles] = React.useState(() => mergeToNew(orderSummaryCardStyles, extendedStyles));

    // XNG-Changes
    const orderNumber = order.erpOrderNumber || order.webOrderNumber;
    const { orderDate, customerPO, status, orderGrandTotalDisplay, webOrderNumber } = order;

    const date = getLocalizedDateTime({
        dateTime: orderDate,
        language,
        options: {
            year: "numeric",
            month: "numeric",
            day: "numeric",
        },
    });

    // XNG-Changes: display ship to + changes on structure
    return (
        <DataTableRow>
            {tableProperties?.date.show && (
                <DataTableCell {...{ header: tableProperties.date.title }} {...styles.dataCells}>
                    <OrderDetailPageTypeLink
                        title={date}
                        // XNG-Changes
                        orderNumber={orderNumber}
                        isVmiOrder={!!order.vmiLocationId}
                        isVmiOrderDetailsPage={isVmiOrderHistoryPage}
                    />
                </DataTableCell>
            )}
            {tableProperties?.orderNumber.show && (
                <DataTableCell
                    className="page-type-link-grid-item"
                    data-test-selector="orderSummaryCard_Date"
                    {...{ header: tableProperties.orderNumber.title }}
                    {...styles.clickableCell}
                >
                    {/* XNG-Changes */}
                    {order.erpOrderNumber && (
                        <OrderDetailPageTypeLink
                            title={orderNumber}
                            orderNumber={orderNumber}
                            isVmiOrder={!!order.vmiLocationId}
                            isVmiOrderDetailsPage={isVmiOrderHistoryPage}
                        />
                    )}
                </DataTableCell>
            )}
            {/* XNG-Change: in classic we display stCompanyName */}
            {tableProperties?.shipTo.show && (
                <DataTableCell {...styles.shipToCells} {...{ header: tableProperties.shipTo.title }}>
                    {order.stCompanyName}
                </DataTableCell>
            )}
            {tableProperties?.status.show && (
                <DataTableCell
                    {...styles.dataCells}
                    {...{ header: tableProperties.status.title }}
                    className="status-grid-item"
                >
                    {status}
                </DataTableCell>
            )}
            {tableProperties?.webOrderNumber.show && (
                <DataTableCell
                    className="page-type-link-grid-item"
                    data-test-selector="orderSummaryCard_Date"
                    {...{ header: tableProperties.webOrderNumber.title }}
                    {...styles.clickableCell}
                >
                    {webOrderNumber}
                </DataTableCell>
            )}
            {tableProperties?.pONumber.show && showPoNumber && (
                <DataTableCell {...styles.dataCells} {...{ header: tableProperties.pONumber.title }}>
                    {customerPO}
                </DataTableCell>
            )}
            {tableProperties?.orderTotal.show && (
                <DataTableCell
                    {...styles.dataCells}
                    {...{ header: tableProperties.orderTotal.title }}
                    className="total-grid-item"
                >
                    {orderGrandTotalDisplay}
                </DataTableCell>
            )}
        </DataTableRow>
    );
};

export default connect(mapStateToProps)(OrderSummaryCard);
